import { create } from 'zustand';
import { persist } from 'zustand/middleware';

let logoutInProgress = false;

const clearAuthState = (set) => {
  set({
    user: null,
    authRole: null,
    token: null,
    isAuthenticated: false
  });
  localStorage.removeItem('token');
  sessionStorage.removeItem('gamrent-superadmin-session');
};

const useStore = create(
  persist(
    (set) => ({
      // Auth State
      user: null,
      authRole: null,
      token: null,
      isAuthenticated: false,

      // Login function
      login: (userData, authToken) => {
        set({
          user: userData,
          authRole: userData?.role || null,
          token: authToken,
          isAuthenticated: true
        });

        localStorage.setItem('token', authToken);

        if (String(userData?.role || "").toLowerCase().replace(/\s+/g, "") === "superadmin") {
          sessionStorage.setItem('gamrent-superadmin-session', '1');
        } else {
          sessionStorage.removeItem('gamrent-superadmin-session');
        }
      },

      // Logout function — always clear local state, even if the API call fails.
      logout: () => {
        if (logoutInProgress) {
          clearAuthState(set);
          return;
        }

        logoutInProgress = true;
        const existingToken = localStorage.getItem('token');
        clearAuthState(set);

        (async () => {
          try {
            const { default: api } = await import('../services/api');
            await api.post('/auth/logout', {}, {
              headers: existingToken ? { Authorization: `Bearer ${existingToken}` } : {}
            });
          } catch {
            // Ignore network / interceptor errors; local session is already cleared.
          } finally {
            logoutInProgress = false;
          }
        })();
      },

      // Update user profile
      updateUser: (userData) => {
        set((state) => ({
          user: { ...state.user, ...userData },
          authRole: userData?.role || state.authRole
        }));
      },

      setAuthContext: (userData) => {
        set((state) => ({
          user: { ...state.user, ...userData },
          authRole: userData?.role || state.authRole
        }));
      },

      // Toast notifications
      toasts: [],
      
      toast: (message, type = 'info') => {
        const normalizedMessage = typeof message === 'string' ? message : String(message || '');
        const normalizedType = ['success', 'error', 'warning', 'info'].includes(type) ? type : 'info';

        const id = Date.now();
        set((state) => ({
          toasts: [...state.toasts, { id, message: normalizedMessage, type: normalizedType }]
        }));

        // Auto remove toast after 3 seconds
        setTimeout(() => {
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id)
          }));
        }, 3000);
      },

      removeToast: (id) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id)
        }));
      }
    }),
    {
      name: 'gamrent-storage',
      partialize: (state) => ({
        user: state.user,
        authRole: state.authRole,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
);

export default useStore;
